'use client';

import { useCallback, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import Select, { SelectItem } from '@/components/ui/select.component';
import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { STATE_ABBR_TO_NAME } from '@/utils/consts/city/city-abbr-to-name.const';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getCitiesByUf } from '@/services/city/get-city-by-uf.service';
import { City } from '@/types/city.type';

const stateItems: SelectItem[] = Object.entries(STATE_ABBR_TO_NAME)
  .map(([abbr, name]) => ({
    id: abbr,
    value: abbr,
    label: name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export function CitiesFilter() {
  const router = useRouter();
  const [selectedState, setSelectedState] = useState<SelectItem | null>(null);
  const [selectedCity, setSelectedCity] = useState<SelectItem | null>(null);
  const [cityItems, setCityItems] = useState<SelectItem[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isPending, startTransition] = useTransition();

  const handleStateChange = useCallback(async (item: SelectItem | null) => {
    setSelectedState(item);
    setSelectedCity(null);
    setCityItems([]);

    if (!item) {
      return;
    }

    setIsLoadingCities(true);
    const cities = await getCitiesByUf(item.value);
    setIsLoadingCities(false);

    if (!cities) {
      return;
    }

    const items = cities
      .map((city: City) => ({
        id: city.slug || city.city,
        value: city.slug || city.city,
        label: city.city,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setCityItems(items);
  }, []);

  const handleCityChange = useCallback((item: SelectItem | null) => {
    setSelectedCity(item);
  }, []);

  const isSearchDisabled = !selectedCity;

  const handleSearch = useCallback(() => {
    if (!selectedCity) {
      return;
    }

    startTransition(() => {
      router.push(`/cidades/${selectedCity.value}/`);
    });
  }, [selectedCity, router]);

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-3">
      <div className="flex flex-1 flex-col gap-1">
        <Typography
          variant={TypographyVariant.MEDIUM_BODY_TEXT}
          className="font-semibold text-gray-700"
        >
          Estado
        </Typography>
        <Select
          items={stateItems}
          selectedItem={selectedState}
          placeholder="Selecione um estado"
          searchPlaceholder="Buscar estado"
          onChange={handleStateChange}
          dataTestId={DataTestIdEnum.CITIES_FILTER_STATE_SELECT}
        />
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <Typography
          variant={TypographyVariant.MEDIUM_BODY_TEXT}
          className="font-semibold text-gray-700"
        >
          Cidade
        </Typography>
        <Select
          items={cityItems}
          selectedItem={selectedCity}
          placeholder="Selecione uma cidade"
          searchPlaceholder="Buscar cidade"
          isLoading={isLoadingCities}
          disabled={!selectedState}
          onChange={handleCityChange}
          dataTestId={DataTestIdEnum.CITIES_FILTER_CITY_SELECT}
        />
      </div>
      <div className="flex flex-1 items-end">
        <Button
          className="w-full h-12"
          disabled={isSearchDisabled || isPending}
          isLoading={isPending}
          onClick={handleSearch}
          data-testid={DataTestIdEnum.CITIES_FILTER_SEARCH_BUTTON}
          data-analytics-event={AnalyticsEnum.CLICK_SEARCH_CITIES_FILTER}
          data-analytics-properties={JSON.stringify({
            estado: selectedState?.value,
            cidade_slug: selectedCity?.value,
            cidade_nome: selectedCity?.label,
          })}
        >
          Buscar
        </Button>
      </div>
    </div>
  );
}
